// Lead wählen (für „Neue Opportunity“ von der Pipeline aus): Suche im Namen, Treffer als Liste, Auswahl als Zeile mit „Ändern“.
import { useEffect, useRef, useState } from "react";
import { leadsApi, type LeadZeile } from "../api";
import { Icon } from "../components/Icon";
import { StatusPille } from "../components/Bausteine";
import { useMeta } from "../daten/meta";

interface Props {
  leadId: string | null;
  leadName: string | null;
  onWahl: (lead: { id: string; name: string | null } | null) => void;
  fehler?: boolean;
}

export function LeadWahl({ leadId, leadName, onWahl, fehler }: Props) {
  const meta = useMeta();
  const [text, setText] = useState("");
  const [treffer, setTreffer] = useState<LeadZeile[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [aktiv, setAktiv] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setTreffer([]);
      setLaedt(false);
      return;
    }
    setLaedt(true);
    const gen = ++generation.current;
    const t = window.setTimeout(async () => {
      try {
        const r = await leadsApi.liste({ q, limit: 8, sort: "name" });
        if (gen !== generation.current) return;
        setTreffer(r.data);
        setAktiv(0);
      } catch {
        if (gen === generation.current) setTreffer([]);
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    }, 220);
    return () => window.clearTimeout(t);
  }, [text]);

  if (leadId) {
    return (
      <div className="opp-leadwahl__gewaehlt">
        <Icon name="leads" groesse={14} />
        <span className="opp-leadwahl__name" title={leadName ?? undefined}>
          {leadName ?? leadId}
        </span>
        <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => onWahl(null)}>
          Ändern
        </button>
      </div>
    );
  }

  const waehlen = (l: LeadZeile) => {
    onWahl({ id: l.id, name: l.display_name ?? l.name });
    setText("");
    setTreffer([]);
  };

  return (
    <div className="opp-leadwahl">
      <label className={`suchfeld opp-leadwahl__suche${fehler ? " suchfeld--fehler" : ""}`}>
        <Icon name="suche" groesse={14} />
        <input
          value={text}
          placeholder="Lead suchen (Firmenname) …"
          aria-label="Lead suchen"
          autoComplete="off"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setAktiv((a) => Math.min(a + 1, treffer.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setAktiv((a) => Math.max(a - 1, 0));
            } else if (e.key === "Enter" && treffer[aktiv]) {
              e.preventDefault();
              waehlen(treffer[aktiv]);
            }
          }}
        />
      </label>
      {text.trim().length >= 2 ? (
        <ul className="opp-leadwahl__liste" role="listbox" aria-label="Treffer">
          {treffer.map((l, i) => (
            <li key={l.id} role="option" aria-selected={i === aktiv}>
              <button type="button" className={`opp-leadwahl__treffer${i === aktiv ? " opp-leadwahl__treffer--aktiv" : ""}`} onMouseEnter={() => setAktiv(i)} onClick={() => waehlen(l)}>
                <span className="opp-leadwahl__name">{l.display_name ?? l.name}</span>
                <StatusPille klein label={l.status_label} farbe={meta.status(l.status_id)?.color} />
              </button>
            </li>
          ))}
          {!treffer.length ? <li className="opp-leadwahl__leer">{laedt ? "Suche …" : "Kein Lead gefunden."}</li> : null}
        </ul>
      ) : null}
    </div>
  );
}
